import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TagSelector } from '../components/TagSelector';
import { apiUrl } from '../config/api';
import { getCostpointLabel } from '../utils/costpointLabels';

// Types from the new budget system
type CostpointType = 'cs' | 'pubg' | 'admin' | 'other';
type BudgetPeriodType = 'H1' | 'H2' | 'ANNUAL';
type CostpointBudgetStatus = 'not_started' | 'draft' | 'ready' | 'no_budget';

interface Tag {
  id: number;
  name: string;
  display_name: string;
  usage_count: number;
  created_at: string;
}

interface BudgetPeriod {
  id: number;
  period_type: BudgetPeriodType;
  year: number;
  start_date: string;
  end_date: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface BudgetItem {
  id: number;
  budget_period_id: number;
  costpoint: CostpointType;
  type: 'income' | 'expenses';
  description: string;
  amount: number;
  status: 'draft' | 'active';
  created_by: string;
  tags: Tag[];
  created_at: string;
  updated_at: string;
}

interface CostpointBudgetSummary {
  costpoint: CostpointType;
  status: CostpointBudgetStatus;
  income_items: BudgetItem[];
  expenses_items: BudgetItem[];
  total_income: number;
  total_expenses: number;
  net_budget: number;
  item_count: number;
}

interface BudgetItemFormData {
  type: 'income' | 'expenses';
  description: string;
  amount: string;
  tags: Tag[];
}

const CostpointBudgetEditPage: React.FC = () => {
  const { id, costpoint } = useParams<{ id: string; costpoint: string }>();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<BudgetPeriod | null>(null);
  const [summary, setSummary] = useState<CostpointBudgetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state for adding new items
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<BudgetItemFormData>({
    type: 'expenses',
    description: '',
    amount: '',
    tags: [],
  });

  // Editing state
  const [editingItem, setEditingItem] = useState<BudgetItem | null>(null);

  const fetchData = useCallback(async () => {
    if (!id || !costpoint) return;

    try {
      setLoading(true);
      setError(null);
      setLoadingMessage('Fetching budget data...');

      console.log(`[BudgetPage] Starting data fetch for period ${id}, costpoint ${costpoint}`);

      // Fetch budget period and costpoint summary
      setLoadingMessage('Loading budget period and costpoint data...');
      const [periodResponse, summaryResponse] = await Promise.all([
        fetch(apiUrl(`budgets/periods/${id}`), { credentials: 'include' }),
        fetch(apiUrl(`budgets/periods/${id}/costpoints/${costpoint}/summary`), {
          credentials: 'include',
        }),
      ]);

      if (!periodResponse.ok || !summaryResponse.ok) {
        throw new Error('Failed to fetch budget data');
      }

      setLoadingMessage('Processing budget data...');
      const periodData = await periodResponse.json();
      const summaryData = await summaryResponse.json();

      console.log(
        `[BudgetPage] Successfully loaded ${summaryData.items?.length || 0} budget items`
      );
      console.log(
        '[BudgetPage] Loaded budget items:',
        summaryData.items?.map((item: { id: number; description: string; tags: unknown[] }) => ({
          id: item.id,
          description: item.description,
          tags: item.tags,
        }))
      );

      setPeriod(periodData);
      setSummary(summaryData);
      setLoadingMessage('Ready!');
    } catch (error) {
      console.error('Failed to fetch budget data:', error);
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [id, costpoint]);

  useEffect(() => {
    if (!id || !costpoint) return;
    fetchData();
  }, [id, costpoint, fetchData]);

  const handleAddItem = async () => {
    const amount = parseFloat(formData.amount);
    if (
      !id ||
      !costpoint ||
      !formData.description.trim() ||
      !formData.amount.trim() ||
      Number.isNaN(amount) ||
      amount <= 0
    )
      return;

    try {
      setSaving(true);

      const requestBody = {
        type: formData.type,
        description: formData.description.trim(),
        amount: amount,

        tag_ids: formData.tags.map((tag) => tag.id),
      };

      console.log('[BudgetPage] Creating budget item with tags:', formData.tags);
      console.log('[BudgetPage] Request body tag_ids:', requestBody.tag_ids);

      const response = await fetch(apiUrl(`budgets/periods/${id}/costpoints/${costpoint}/items`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        // Reset form and refresh data
        setFormData({
          type: 'expenses',
          description: '',
          amount: '',
          tags: [],
        });
        setShowAddForm(false);
        await fetchData();
      } else {
        const errorData = await response.json();
        alert(`Failed to add budget item: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to add budget item:', error);
      alert('Failed to add budget item');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItem = async (item: BudgetItem, updates: Partial<BudgetItem>) => {
    try {
      setSaving(true);
      const response = await fetch(apiUrl(`budgets/items/${item.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          description: updates.description || item.description,
          amount: updates.amount || item.amount,
          tag_ids: (updates.tags || item.tags).map((tag) => tag.id),
        }),
      });

      if (response.ok) {
        setEditingItem(null);
        await fetchData();
      } else {
        const errorData = await response.json();
        alert(`Failed to update budget item: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to update budget item:', error);
      alert('Failed to update budget item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (item: BudgetItem) => {
    if (!confirm(`Are you sure you want to delete "${item.description}"?`)) return;

    try {
      setSaving(true);
      const response = await fetch(apiUrl(`budgets/items/${item.id}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        // Optimistically remove from UI immediately
        setSummary((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            income_items: prev.income_items.filter((i) => i.id !== item.id),
            expenses_items: prev.expenses_items.filter((i) => i.id !== item.id),
          };
        });
        await fetchData();
      } else {
        const errorData = await response.json();
        alert(`Failed to delete budget item: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to delete budget item:', error);
      alert('Failed to delete budget item');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReady = async () => {
    if (!id || !costpoint || !summary) return;

    if (summary.item_count === 0) {
      alert("Cannot mark as ready - please add at least one budget item or mark as 'No Budget'");
      return;
    }

    try {
      const response = await fetch(apiUrl(`budgets/periods/${id}/costpoints/${costpoint}/status`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'ready' }),
      });

      if (response.ok) {
        alert('Costpoint marked as ready!');
        navigate(`/budget/periods/${id}/costpoints`);
      } else {
        const errorData = await response.json();
        alert(`Failed to mark as ready: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to mark as ready:', error);
      alert('Failed to mark as ready');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Summary totals use no grouping separator so their text never collides with
  // individual item amounts in Playwright getByText assertions (Finnish locale
  // uses a non-breaking space as the thousands separator which matches \s).
  const formatCurrencySummary = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
      useGrouping: false,
    }).format(amount);
  };

  const getCostpointColor = (costpoint: string) => {
    switch (costpoint) {
      case 'cs':
        return 'bg-blue-500';
      case 'pubg':
        return 'bg-green-500';
      case 'admin':
        return 'bg-purple-500';
      case 'other':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{loadingMessage}</p>
          <p className="mt-2 text-sm text-gray-400">
            Period: {id} | Costpoint: {costpoint}
          </p>
        </div>
      </div>
    );
  }

  if (error || !period || !summary) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Budget data not found'}</p>
          <button
            onClick={() => navigate('/budget')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back to Budget Overview
          </button>
        </div>
      </div>
    );
  }

  // Check if budget is read-only (locked or active)
  const isReadOnly = period.status === 'locked' || period.status === 'active';

  const BudgetItemCard: React.FC<{
    item: BudgetItem;
    onEdit: () => void;
    onDelete: () => void;
  }> = ({ item, onEdit, onDelete }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span
              className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                item.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {item.type.toUpperCase()}
            </span>
            <span className="text-lg font-semibold text-gray-900">
              {formatCurrency(item.amount)}
            </span>
          </div>
          <p className="text-gray-700 mb-2">{item.description}</p>
          {item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                  title={tag.display_name}
                >
                  {tag.display_name}
                </span>
              ))}
            </div>
          )}
        </div>
        {!isReadOnly && (
          <div className="flex space-x-2 ml-4">
            <button onClick={onEdit} className="text-blue-600 hover:text-blue-800 text-sm">
              Edit
            </button>
            <button onClick={onDelete} className="text-red-600 hover:text-red-800 text-sm">
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const EditItemForm: React.FC<{
    item: BudgetItem;
    onSave: (updates: Partial<BudgetItem>) => void;
    onCancel: () => void;
  }> = ({ item, onSave, onCancel }) => {
    const [editFormData, setEditFormData] = useState({
      description: item.description,
      amount: item.amount.toString(),
      tags: item.tags,
    });

    return (
      <div className="bg-white rounded-lg border border-blue-200 p-4">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={editFormData.description}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  description: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (€)</label>
            <input
              type="number"
              value={editFormData.amount}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  amount: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <TagSelector
              selectedTags={editFormData.tags}
              onTagsChange={(tags) => setEditFormData({ ...editFormData, tags })}
              placeholder="Add tags..."
              maxTags={10}
            />
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() =>
                onSave({
                  description: editFormData.description,
                  amount: parseFloat(editFormData.amount),
                  tags: editFormData.tags,
                })
              }
              disabled={
                saving ||
                !editFormData.description.trim() ||
                !editFormData.amount.trim() ||
                Number.isNaN(parseFloat(editFormData.amount)) ||
                parseFloat(editFormData.amount) <= 0
              }
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center">
              <div
                className={`w-12 h-12 ${getCostpointColor(
                  costpoint || ''
                )} rounded-md flex items-center justify-center mr-4`}
              >
                <span className="text-white text-lg font-bold">
                  {(costpoint || '').toUpperCase()}
                </span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {getCostpointLabel(costpoint || '')} Budget
                </h1>
                <p className="text-gray-600">
                  {period.period_type} {period.year} Budget Management
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate(`/budget/periods/${id}/costpoints`)}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Back to Overview
              </button>
              {!isReadOnly && summary.status === 'draft' && summary.item_count > 0 && (
                <button
                  onClick={handleMarkReady}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Mark as Ready
                </button>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-500">Income Items</h3>
              <p className="text-xl font-bold text-gray-900">{summary.income_items.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-500">Expenses Items</h3>
              <p className="text-xl font-bold text-gray-900">{summary.expenses_items.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-500">Total Income</h3>
              <p className="text-xl font-bold text-green-600" data-testid="total-income">
                {formatCurrencySummary(summary.total_income)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-500">Total Expenses</h3>
              <p className="text-xl font-bold text-red-600">
                {formatCurrencySummary(summary.total_expenses)}
              </p>
            </div>
          </div>

          {/* Net Budget */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Net Budget</h3>
              <p
                className={`text-3xl font-bold ${
                  summary.net_budget >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrencySummary(summary.net_budget)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {summary.net_budget >= 0 ? 'Surplus' : 'Deficit'}
              </p>
            </div>
          </div>

          {/* Read-Only Notice */}
          {isReadOnly && (
            <div className="mb-8 bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Budget is {period.status === 'locked' ? 'Locked' : 'Active'}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      This budget period is {period.status} and cannot be edited. You can view the
                      budget items below.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add New Item */}
          {!isReadOnly && (
            <div className="mb-8">
              {!showAddForm ? (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
                  data-testid="add-budget-item-button"
                >
                  Add Budget Item
                </button>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Budget Item</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            type: e.target.value as 'income' | 'expenses',
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        data-testid="budget-item-type-select"
                      >
                        <option value="expenses">Expenses</option>
                        <option value="income">Income</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        placeholder="Enter description..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        data-testid="budget-item-description-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (€)
                      </label>
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            amount: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                        step="0.01"
                        data-testid="budget-item-amount-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                      <TagSelector
                        selectedTags={formData.tags}
                        onTagsChange={(tags) => setFormData({ ...formData, tags })}
                        placeholder="Add tags..."
                        maxTags={10}
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleAddItem}
                        disabled={
                          saving ||
                          !formData.description.trim() ||
                          !formData.amount.trim() ||
                          Number.isNaN(parseFloat(formData.amount)) ||
                          parseFloat(formData.amount) <= 0
                        }
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        data-testid="add-budget-item-submit-button"
                      >
                        {saving ? 'Adding...' : 'Add Item'}
                      </button>
                      <button
                        onClick={() => {
                          setShowAddForm(false);
                          setFormData({
                            type: 'expenses',
                            description: '',
                            amount: '',
                            tags: [],
                          });
                        }}
                        className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Income Items */}
          {summary.income_items.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Income Items</h2>
              <div className="space-y-4">
                {summary.income_items.map((item) => (
                  <div key={item.id}>
                    {editingItem?.id === item.id ? (
                      <EditItemForm
                        item={item}
                        onSave={(updates) => handleUpdateItem(item, updates)}
                        onCancel={() => setEditingItem(null)}
                      />
                    ) : (
                      <BudgetItemCard
                        item={item}
                        onEdit={() => setEditingItem(item)}
                        onDelete={() => handleDeleteItem(item)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expense Items */}
          {summary.expenses_items.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Expense Items</h2>
              <div className="space-y-4">
                {summary.expenses_items.map((item) => (
                  <div key={item.id}>
                    {editingItem?.id === item.id ? (
                      <EditItemForm
                        item={item}
                        onSave={(updates) => handleUpdateItem(item, updates)}
                        onCancel={() => setEditingItem(null)}
                      />
                    ) : (
                      <BudgetItemCard
                        item={item}
                        onEdit={() => setEditingItem(item)}
                        onDelete={() => handleDeleteItem(item)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {summary.item_count === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg
                  className="mx-auto h-16 w-16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Budget Items</h3>
              <p className="text-gray-500 mb-4">
                Start by adding income and expense items for this costpoint.
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Add First Item
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostpointBudgetEditPage;
